// src/components/Kyusou3PacePanel.tsx (2026-09-26 新設・開発版)
// 急性期総合体制加算3: 手術等の集積性の要件（全身麻酔 年2,000件＋ア〜キのうち5つ以上）を年度累計のペースで見張る。
//   源 = fact_kyusou3_daily / dim_kyusou3_requirement / dim_kyusou3_meta / fact_kyusou3_quality
//        (build_kyusou3_pace.R ← 急性期総合体制加算3 PJ R/10_pace_feed.R)。
//   「年間」= 前年度4/1〜3/31（様式13 注3）なので、見るのは当年度の累計。
//   ペースの物差しは2つ（ユーザ指定で両方並べる）:
//     ① 按分ライン = 年間基準 × 経過日数 / 年度の日数
//     ② 前年同期比の着地見込み = 前年度の通年実績 × (当年度累計 / 前年度の同じ日までの累計)
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Plot from "@observablehq/plot";
import { PlotFigure } from "./Plot";
import { api } from "../lib/api";
import type { Kyusou3DailyRow, Kyusou3Requirement } from "@shared/api";

const DAY = 86_400_000;
const d0 = (s: string) => new Date(`${s}T00:00:00`);
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const n0 = (v: number) => (Number.isFinite(v) ? Math.round(v).toLocaleString() : "—");
const jp = (s: string) => { const d = d0(s); return `${d.getMonth() + 1}月${d.getDate()}日`; };
const jpY = (s: string) => { const d = d0(s); return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`; };
/** 前年度の同じ日（2/29 は 2/28 に寄せる） */
const minusYear = (s: string) => {
  const d = d0(s); const t = new Date(d.getFullYear() - 1, d.getMonth(), d.getDate());
  if (t.getMonth() !== d.getMonth()) t.setDate(0);
  return iso(t);
};

type Status = "余裕" | "達成見込み" | "要注意" | "未達見込み" | "実績なし" | "判定保留" | "判定不能";
const STATUS_STYLE: Record<Status, { cls: string; mark: string }> = {
  余裕: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", mark: "◎" },
  達成見込み: { cls: "bg-teal-50 text-teal-800 border-teal-200", mark: "○" },
  要注意: { cls: "bg-amber-50 text-amber-800 border-amber-200", mark: "△" },
  未達見込み: { cls: "bg-red-50 text-red-800 border-red-200", mark: "×" },
  実績なし: { cls: "bg-slate-100 text-slate-600 border-slate-200", mark: "—" },
  判定保留: { cls: "bg-slate-100 text-slate-600 border-slate-200", mark: "…" },
  判定不能: { cls: "bg-slate-100 text-slate-600 border-slate-200", mark: "?" },
};
/** 年度初めは見込みが数日分のノイズになるので、この日数に満たない間は状態を出さない */
const MIN_ELAPSED_DAYS = 45;
/** フィードの取得からこの日数を超えたら古いと警告する */
// 毎月12日に更新（2026-09-26〜）。次の更新直前で約31日なので、1回分の取りこぼしを 40日で見る。
const STALE_DAYS = 40;
/** データ時点（速報の会計ヘッダが揃った日）が今日からこの日数より古ければ、止まっていると警告する（平常は10日前後遅れ） */
const ASOF_STALE_DAYS = 50;
const STATUS_HELP: Record<Status, string> = {
  余裕: "2つの見込みがどちらも基準の1.1倍以上",
  達成見込み: "2つの見込みがどちらも基準以上",
  要注意: "どちらか一方の見込みが基準を下回る",
  未達見込み: "2つの見込みがどちらも基準を下回る",
  実績なし: "当年度・前年度とも実績が無い",
  判定保留: `年度の経過が${MIN_ELAPSED_DAYS}日未満なので見込みを出さない（按分ラインとの差だけ見る）`,
  判定不能: "必須条件の件数が取れていない（集計の異常を疑う）",
};
const OVERALL_HELP = "必須（全身麻酔）が達成見込み以上、かつ ア〜キ のうち達成見込み以上が5つ以上なら達成見込み（全部が余裕なら余裕）。どちらかが欠けそうなら要注意、明らかに届かなければ未達見込み";
const OPTS_HELP = "ア〜キ のうち「余裕」「達成見込み」の項目数。6つ以上=余裕・5つ=達成見込み・4つ以下=要注意";

interface Pace {
  req: Kyusou3Requirement; ytd: number; paceTarget: number; paceDiff: number;
  prevSame: number; prevFull: number; forecastPrev: number; forecastLinear: number;
  remaining: number; needPerMonth: number; avgPerMonth: number; status: Status;
}

export function Kyusou3PacePanel() {
  const { data, isLoading, error } = useQuery({ queryKey: ["kyusou3_pace"], queryFn: api.kyusou3Pace, staleTime: 5 * 60_000 });

  const calc = useMemo(() => {
    if (!data?.meta || data.requirements.length === 0) return null;
    const m = data.meta;
    const fyStart = `${m.fy}-04-01`, fyEnd = `${m.fy + 1}-03-31`;
    const prevStart = `${m.prevFy}-04-01`;
    const daysInFy = Math.round((d0(fyEnd).getTime() - d0(fyStart).getTime()) / DAY) + 1;
    const elapsed = Math.round((d0(m.asOf).getTime() - d0(fyStart).getTime()) / DAY) + 1;
    const prevCut = minusYear(m.asOf);
    const monthsElapsed = elapsed / (daysInFy / 12);
    const byCode = new Map<string, Kyusou3DailyRow[]>();
    for (const r of data.daily) {
      const a = byCode.get(r.code) ?? []; a.push(r); byCode.set(r.code, a);
    }
    const paces: Pace[] = data.requirements.map((req) => {
      const rows = byCode.get(req.code) ?? [];
      const cur = rows.filter((r) => r.fy === m.fy && r.date <= m.asOf);
      const prev = rows.filter((r) => r.fy === m.prevFy && r.date >= prevStart);
      const ytd = cur.reduce((s, r) => s + r.count, 0);
      const prevFull = prev.reduce((s, r) => s + r.count, 0);
      const prevSame = prev.filter((r) => r.date <= prevCut).reduce((s, r) => s + r.count, 0);
      const paceTarget = (req.threshold * elapsed) / daysInFy;
      const forecastLinear = (ytd * daysInFy) / elapsed;
      const forecastPrev = prevSame > 0 ? (prevFull * ytd) / prevSame : NaN;
      const remaining = Math.max(req.threshold - ytd, 0);
      const monthsLeft = 12 - monthsElapsed;
      const f = [forecastLinear, forecastPrev].filter(Number.isFinite);
      let status: Status;
      if (ytd === 0 && prevFull === 0) status = "実績なし";
      else if (elapsed < MIN_ELAPSED_DAYS) status = "判定保留";
      else if (f.length === 0) status = "要注意";
      else if (f.every((v) => v >= req.threshold * 1.1)) status = "余裕";
      else if (f.every((v) => v >= req.threshold)) status = "達成見込み";
      else if (f.every((v) => v < req.threshold)) status = "未達見込み";
      else status = "要注意";
      return {
        req, ytd, paceTarget, paceDiff: ytd - paceTarget, prevSame, prevFull, forecastPrev, forecastLinear,
        remaining, needPerMonth: monthsLeft > 1e-9 ? remaining / monthsLeft : NaN, avgPerMonth: ytd / monthsElapsed, status,
      };
    });
    // 月別件数（当年度・前年度）
    const monthKeys = Array.from({ length: 12 }, (_, i) => (i + 3) % 12 + 1); // 4..3
    const monthly = new Map<string, { cur: number[]; prev: number[]; curSokuho: boolean[]; prevSokuho: boolean[] }>();
    // ＊ = 速報区間にかかる月（行の有無ではなく区間で決める＝0件の月にも付ける）
    const sokuhoMonth = monthKeys.map((mm) => {
      if (!m.prelimFrom || !m.prelimTo) return false;
      const y = mm >= 4 ? m.fy : m.fy + 1;
      const first = `${y}-${String(mm).padStart(2, "0")}-01`;
      const last = iso(new Date(y, mm, 0));
      return first <= m.prelimTo && last >= m.prelimFrom;
    });
    const prevSokuhoMonth = monthKeys.map((mm) => {
      if (!m.prelimFrom || !m.prelimTo) return false;
      const y = mm >= 4 ? m.prevFy : m.prevFy + 1;
      const first = `${y}-${String(mm).padStart(2, "0")}-01`;
      const last = iso(new Date(y, mm, 0));
      return first <= m.prelimTo && last >= m.prelimFrom;
    });
    for (const req of data.requirements) monthly.set(req.code, { cur: Array(12).fill(0), prev: Array(12).fill(0), curSokuho: [...sokuhoMonth], prevSokuho: [...prevSokuhoMonth] });
    for (const r of data.daily) {
      const t = monthly.get(r.code); if (!t) continue;
      const idx = monthKeys.indexOf(d0(r.date).getMonth() + 1);
      if (r.fy === m.fy) t.cur[idx] += r.count;
      else if (r.fy === m.prevFy) t.prev[idx] += r.count;
    }
    return { m, fyStart, fyEnd, daysInFy, elapsed, prevCut, paces, monthKeys, monthly, byCode };
  }, [data]);

  if (isLoading) return <div className="card p-8 animate-pulse text-slate-400">急性期総合体制加算3を読み込み中…</div>;
  if (error) {
    return (
      <div className="card border-red-200 bg-red-50 p-6">
        <h2 className="font-semibold text-red-800">急性期総合体制加算3を取得できません</h2>
        <p className="mt-1 text-sm text-red-700">{(error as Error).message}</p>
      </div>
    );
  }
  if (!calc) {
    return (
      <div className="card p-8">
        <h2 className="text-lg font-semibold text-slate-800">急性期総合体制加算3は準備中です</h2>
        <p className="mt-2 text-sm text-slate-600">
          電子カルテからの集計（急性期総合体制加算3 PJ の R/10_pace_feed.R）とビルド（build_kyusou3_pace.R）がまだ走っていません。
        </p>
      </div>
    );
  }
  const { m, fyStart, fyEnd, daysInFy, elapsed, prevCut, paces, monthKeys, monthly, byCode } = calc;
  const must = paces.find((p) => p.req.kind === "必須");
  const opts = paces.filter((p) => p.req.kind === "選択");
  const okStatus = (s: Status) => s === "余裕" || s === "達成見込み";
  const nOk = opts.filter((p) => okStatus(p.status)).length;
  const nAhead = opts.filter((p) => p.paceDiff >= 0).length;
  const nNeed = 5;
  const optsBadge: Status = elapsed < MIN_ELAPSED_DAYS ? "判定保留" : nOk >= nNeed + 1 ? "余裕" : nOk >= nNeed ? "達成見込み" : "要注意";
  const overall: Status =
    !must || must.status === "実績なし" || must.status === "判定不能" ? "判定不能"
    : elapsed < MIN_ELAPSED_DAYS ? "判定保留"
    : okStatus(must.status) && nOk >= nNeed ? (must.status === "余裕" && opts.filter((p) => p.status === "余裕").length >= nNeed ? "余裕" : "達成見込み")
    : must.status !== "未達見込み" && opts.filter((p) => p.status !== "未達見込み" && p.status !== "実績なし").length >= nNeed ? "要注意"
    : "未達見込み";
  const fetchedAge = Math.floor((Date.now() - new Date(`${m.fetchedAt.slice(0, 10)}T00:00:00`).getTime()) / DAY);
  const inputLag = Math.round((d0(m.requestedTo).getTime() - d0(m.asOf).getTime()) / DAY);
  const asOfAge = Math.floor((Date.now() - d0(m.asOf).getTime()) / DAY);
  const curMonthIdx = monthKeys.indexOf(d0(m.asOf).getMonth() + 1);
  const asOfIsMonthEnd = new Date(d0(m.asOf).getTime() + DAY).getDate() === 1;
  const upper = (code: string) => code === "a";

  const badge = (s: Status, help: string = STATUS_HELP[s]) => (
    <span title={help} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[s].cls}`}>
      <span aria-hidden>{STATUS_STYLE[s].mark}</span>{s}
    </span>
  );
  const signed = (v: number) => (Number.isFinite(v) ? `${v >= 0 ? "+" : "−"}${n0(Math.abs(v))}` : "—");

  // 小さなグラフ（要件ごと）: 当年度累計・前年度累計（1年ずらして重ねる）・按分ライン・基準
  const chart = (p: Pace): Plot.PlotOptions => {
    const rows = byCode.get(p.req.code) ?? [];
    const cum = (fy: number, shift: number, until: string) => {
      const out: { date: Date; v: number }[] = [];
      const map = new Map<string, number>();
      for (const r of rows) if (r.fy === fy) map.set(r.date, (map.get(r.date) ?? 0) + r.count);
      let s = 0;
      const start = d0(`${fy}-04-01`);
      for (let t = start.getTime(); t <= d0(until).getTime(); t += DAY) {
        const k = iso(new Date(t)); s += map.get(k) ?? 0;
        const dd = new Date(t); dd.setFullYear(dd.getFullYear() + shift);
        out.push({ date: dd, v: s });
      }
      return out;
    };
    const cur = cum(m.fy, 0, m.asOf);
    const prev = cum(m.prevFy, 1, `${m.fy}-03-31`);
    const top = Math.max(p.req.threshold, p.prevFull, p.ytd, p.forecastLinear || 0) * 1.08;
    return {
      width: 420, height: 190, marginLeft: 44, marginRight: 12, marginBottom: 24, marginTop: 8,
      x: { type: "time", domain: [d0(fyStart), d0(fyEnd)], ticks: [4, 7, 10, 1].map((mm) => d0(`${mm >= 4 ? m.fy : m.fy + 1}-${String(mm).padStart(2, "0")}-01`)), tickFormat: (d: Date) => `${d.getMonth() + 1}月`, label: null },
      y: { domain: [0, top], grid: true, label: null, tickFormat: (v: number) => v.toLocaleString() },
      marks: [
        Plot.ruleY([p.req.threshold], { stroke: "#64748b", strokeWidth: 1 }),
        Plot.text([{ x: d0(fyStart), y: p.req.threshold }], { x: "x", y: "y", text: () => `基準 ${n0(p.req.threshold)}`, dy: -6, textAnchor: "start", fill: "#475569", fontSize: 10 }),
        Plot.line([{ x: d0(fyStart), y: 0 }, { x: d0(fyEnd), y: p.req.threshold }], { x: "x", y: "y", stroke: "#94a3b8", strokeDasharray: "4,3", strokeWidth: 1.5 }),
        Plot.line(prev, { x: "date", y: "v", stroke: "#cbd5e1", strokeWidth: 2 }),
        Plot.line(cur, { x: "date", y: "v", stroke: "#0f766e", strokeWidth: 2 }),
        Plot.dot(cur.slice(-1), { x: "date", y: "v", fill: "#0f766e", r: 4, stroke: "white", strokeWidth: 2 }),
        Plot.ruleX(cur, Plot.pointerX({ x: "date", stroke: "#e2e8f0" })),
        Plot.tip(cur, Plot.pointerX({ x: "date", y: "v", title: (d: { date: Date; v: number }) => {
          const k = iso(d.date); const pv = prev.find((x) => iso(x.date) === k)?.v;
          const pt = (p.req.threshold * (Math.round((d.date.getTime() - d0(fyStart).getTime()) / DAY) + 1)) / daysInFy;
          return `${d.date.getMonth() + 1}月${d.date.getDate()}日まで\n当年度 ${n0(d.v)}\n按分ライン ${n0(pt)}\n前年度 ${pv == null ? "—" : n0(pv)}`;
        } })),
      ],
    };
  };

  return (
    <div className="space-y-3">
      <section className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-xl font-bold text-slate-900">急性期総合体制加算3 手術等の実績ペース</h2>
          {badge(overall, OVERALL_HELP)}
        </div>
        {fetchedAge > STALE_DAYS && (
          <p className="mt-2 rounded border border-red-200 bg-red-50 px-3 py-1.5 text-sm text-red-800">
            データが古くなっています（取得 {m.fetchedAt}・{fetchedAge}日前）。集計（R/10_pace_feed.R）が止まっていないか確認してください。
          </p>
        )}
        {fetchedAge <= STALE_DAYS && asOfAge > ASOF_STALE_DAYS && (
          <p className="mt-2 rounded border border-red-200 bg-red-50 px-3 py-1.5 text-sm text-red-800">
            データ時点が {jp(m.asOf)} から進んでいません（{asOfAge}日前・平常は10日前後）。速報の会計ヘッダが止まっている可能性があります。
          </p>
        )}
        {m.qualityError && (
          <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm text-amber-800">
            速報と確定の突合に失敗しました（{m.qualityError}）。速報部分の件数は確かめられていません。
          </p>
        )}
        <p className="mt-1 text-xs text-slate-500">
          電子カルテ会計データ（本院・毎月12日に更新）。{m.fy}年度（{jpY(fyStart)}〜{jpY(fyEnd)}）の {jp(m.asOf)} までの累計。
          確定 {jp(m.confirmedTo)} まで{m.prelimTo ? `／速報 ${jp(m.prelimFrom!)}〜${jp(m.prelimTo)}` : ""}。取得 {m.fetchedAt}（{fetchedAge <= 0 ? "今日" : `${fetchedAge}日前`}）。
          経過 {elapsed}日／{daysInFy}日（{((elapsed / daysInFy) * 100).toFixed(0)}%）。
          {inputLag > 0 && <>速報会計は {jp(m.asOf)} までを使用（速報の会計ヘッダが {jp(m.requestedTo)} 時点でそこまでしか揃っていないため・{inputLag}日遅れ）。</>}
        </p>
        <p className="mt-2 max-w-5xl text-sm text-slate-700">
          施設基準は「全身麻酔による手術件数が<b>年2,000件以上</b>（必須）、かつ ア〜キ のうち<b>5つ以上</b>を満たす」こと。
          ここでの「年」は<b>前年度の4月1日〜3月31日</b>（様式13 注3）なので、{m.fy}年度の実績が翌年度の判定に使われます。
          見込みは2つの物差しで出しています — <b>①按分ライン</b>（年間基準を経過日数で割り振った件数）と比べた今の位置、
          <b>②前年同期比の着地見込み</b>（前年度の同じ日までの累計に対する今年の比率を、前年度の通年実績に掛けたもの＝季節の波を反映）。
        </p>
      </section>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {must && (
          <div className="card p-3">
            <div className="text-[11px] text-slate-500">必須　{must.req.name}（年{n0(must.req.threshold)}件）</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-bold tabular-nums text-slate-900">{n0(must.ytd)}件</span>
              {badge(must.status)}
            </div>
            <div className="text-[11px] text-slate-500">
              按分ライン {n0(must.paceTarget)}件に対し {signed(must.paceDiff)}／着地見込み {n0(must.forecastPrev)}件（前年比）
            </div>
          </div>
        )}
        <div className="card p-3">
          <div className="text-[11px] text-slate-500">選択　ア〜キのうち達成見込み（5つ以上が必要）</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-bold tabular-nums text-slate-900">{optsBadge === "判定保留" ? "—" : nOk} / {opts.length}</span>
            {badge(optsBadge, OPTS_HELP)}
          </div>
          <div className="text-[11px] text-slate-500">按分ラインを上回っている項目 {nAhead}／{opts.length}。余裕 = 5つを超える項目数 {Math.max(nOk - nNeed, 0)}</div>
        </div>
        <div className="card p-3">
          <div className="text-[11px] text-slate-500">見込みの判定</div>
          <ul className="mt-1 space-y-0.5 text-[11px] text-slate-600">
            {(["余裕", "達成見込み", "要注意", "未達見込み", "判定保留"] as Status[]).map((s) => (
              <li key={s} className="flex items-center gap-2">{badge(s)}<span>{STATUS_HELP[s]}</span></li>
            ))}
          </ul>
        </div>
      </div>

      <section className="card overflow-hidden">
        <div className="border-b border-slate-100 px-4 py-2">
          <h2 className="font-semibold text-slate-900">要件ごとのペース（{jp(m.asOf)}時点）</h2>
          <p className="text-[11px] text-slate-500">
            按分ライン = 年間基準 × {elapsed}/{daysInFy}日。前年同期 = {m.prevFy}年度の {jp(prevCut)} までの累計。
            単純年換算 = 当年度累計 × {daysInFy}/{elapsed}日。残り必要数/月 = 基準までの残りを年度末までの月数で割ったもの。
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] text-slate-500">
                <th className="px-3 py-1.5 text-left font-medium">要件</th>
                <th className="px-3 py-1.5 text-right font-medium">年間基準</th>
                <th className="px-3 py-1.5 text-right font-medium">当年度累計</th>
                <th className="px-3 py-1.5 text-right font-medium">按分ライン</th>
                <th className="px-3 py-1.5 text-right font-medium">按分との差</th>
                <th className="px-3 py-1.5 text-right font-medium">前年同期</th>
                <th className="px-3 py-1.5 text-right font-medium">前年同期比</th>
                <th className="px-3 py-1.5 text-right font-medium">着地見込み（前年比）</th>
                <th className="px-3 py-1.5 text-right font-medium">着地見込み（単純年換算）</th>
                <th className="px-3 py-1.5 text-right font-medium">前年度通年</th>
                <th className="px-3 py-1.5 text-right font-medium">月平均（当年度）</th>
                <th className="px-3 py-1.5 text-right font-medium">残り必要数／月</th>
                <th className="px-3 py-1.5 text-left font-medium">見込み</th>
              </tr>
            </thead>
            <tbody>
              {paces.map((p) => (
                <tr key={p.req.code} className={`border-b border-slate-100 ${p.req.kind === "必須" ? "bg-slate-50" : ""}`}>
                  <td className="px-3 py-1.5 whitespace-nowrap text-slate-800">
                    <span className="mr-1.5 inline-block w-7 text-[11px] text-slate-500">{p.req.symbol}</span>{p.req.name}
                    {upper(p.req.code) && <span title="病理結果と未結合の上限値" className="ml-1 text-[11px] text-amber-700">上限値</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{n0(p.req.threshold)}</td>
                  <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{n0(p.ytd)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{n0(p.paceTarget)}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${p.paceDiff < 0 ? "text-red-700" : "text-slate-800"}`}>{signed(p.paceDiff)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{n0(p.prevSame)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{p.prevSame > 0 ? `${((p.ytd / p.prevSame) * 100).toFixed(0)}%` : "—"}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${p.forecastPrev < p.req.threshold ? "text-red-700" : ""}`}>{n0(p.forecastPrev)}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${p.forecastLinear < p.req.threshold ? "text-red-700" : ""}`}>{n0(p.forecastLinear)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{n0(p.prevFull)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{p.ytd > 0 ? p.avgPerMonth.toFixed(1) : "0"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{p.remaining === 0 ? "達成済" : Number.isFinite(p.needPerMonth) ? p.needPerMonth.toFixed(1) : "—"}</td>
                  <td className="px-3 py-1.5">{badge(p.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card p-4">
        <h2 className="font-semibold text-slate-900">累計の推移</h2>
        <div className="mt-1 flex flex-wrap gap-4 text-[11px] text-slate-500">
          <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-5 bg-[#0f766e]" />{m.fy}年度の累計</span>
          <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-5 bg-[#cbd5e1]" />{m.prevFy}年度の累計（同じ月日に重ねた）</span>
          <span className="flex items-center gap-1"><span className="inline-block w-5 border-t-2 border-dashed border-[#94a3b8]" />按分ライン（3月31日に基準へ届く線）</span>
          <span className="flex items-center gap-1"><span className="inline-block h-px w-5 bg-[#64748b]" />年間基準</span>
        </div>
        <div className="mt-2 grid gap-x-4 gap-y-3 md:grid-cols-2 xl:grid-cols-4">
          {paces.map((p) => (
            <div key={p.req.code}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium text-slate-800"><span className="mr-1 text-[11px] text-slate-500">{p.req.symbol}</span>{p.req.name}{upper(p.req.code) && <span className="ml-1 text-[11px] text-amber-700">上限値</span>}</div>
                {badge(p.status)}
              </div>
              <PlotFigure options={chart(p)} responsiveWidth />
            </div>
          ))}
        </div>
      </section>

      <section className="card overflow-hidden">
        <div className="border-b border-slate-100 px-4 py-2">
          <h2 className="font-semibold text-slate-900">月別件数</h2>
          <p className="text-[11px] text-slate-500">上段 = {m.fy}年度、下段 = {m.prevFy}年度。<span className="text-amber-700">＊</span> = 速報会計の月（確定前）。{!asOfIsMonthEnd && <>{d0(m.asOf).getMonth() + 1}月は {jp(m.asOf)} までの途中の件数（前年は1か月分）。</>}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] text-slate-500">
                <th className="px-3 py-1.5 text-left font-medium">要件</th>
                <th className="px-2 py-1.5 text-left font-medium" />
                {monthKeys.map((mm, i) => <th key={mm} className="px-2 py-1.5 text-right font-medium">{mm}月{i === curMonthIdx && !asOfIsMonthEnd && <span className="text-slate-400">（〜{d0(m.asOf).getDate()}日）</span>}</th>)}
                <th className="px-3 py-1.5 text-right font-medium">計</th>
              </tr>
            </thead>
            <tbody>
              {paces.map((p) => {
                const t = monthly.get(p.req.code)!;
                const lastIdx = monthKeys.indexOf(d0(m.asOf).getMonth() + 1);
                return [
                  <tr key={`${p.req.code}-c`} className="border-t border-slate-200">
                    <td rowSpan={2} className="px-3 py-1 align-top whitespace-nowrap text-slate-800"><span className="mr-1 text-[11px] text-slate-500">{p.req.symbol}</span>{p.req.name}</td>
                    <td className="px-2 py-1 text-[11px] text-slate-500">{m.fy}</td>
                    {t.cur.map((v, i) => (
                      <td key={i} className="px-2 py-1 text-right tabular-nums">
                        {i > lastIdx ? "" : <>{n0(v)}{t.curSokuho[i] && <span className="text-amber-700">＊</span>}</>}
                      </td>
                    ))}
                    <td className="px-3 py-1 text-right font-semibold tabular-nums">{n0(p.ytd)}</td>
                  </tr>,
                  <tr key={`${p.req.code}-p`} className="text-slate-400">
                    <td className="px-2 py-1 text-[11px]">{m.prevFy}</td>
                    {t.prev.map((v, i) => <td key={i} className="px-2 py-1 text-right tabular-nums">{n0(v)}{t.prevSokuho[i] && <span className="text-amber-700">＊</span>}</td>)}
                    <td className="px-3 py-1 text-right tabular-nums">{n0(p.prevFull)}</td>
                  </tr>,
                ];
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card p-4 text-[12px] leading-relaxed text-slate-600">
        <h2 className="mb-1 text-sm font-semibold text-slate-900">数え方と注意</h2>
        <ul className="list-disc space-y-1 pl-5">
          {paces.filter((p) => p.req.note).map((p) => <li key={p.req.code}><b>{p.req.symbol} {p.req.name}</b>：{p.req.note}</li>)}
          <li>6手術要件の対象コードは通知を公的マスタへ機械展開した正本（{m.master}）。同一RP（伝票の1手技）は要件ごとに1件。施設基準の実績なので包括・出来高を問わず実施した手術を数え、入院・外来とも含む。</li>
          <li>確定 {jp(m.confirmedTo)} までは確定会計、その先は速報会計（訂正で置き換えられた旧版の行は除く）。
            {data!.quality.length > 0 && (
              <> 締まった {data!.quality[0].month.slice(0, 4)}年{Number(data!.quality[0].month.slice(4))}月を速報会計でも数えた差：
                {data!.quality.map((q) => { const r = paces.find((p) => p.req.code === q.code); return `${r?.req.symbol ?? q.code} ${q.diff >= 0 ? "+" : ""}${q.diff}`; }).join("・")}（数え方が速報でも同じことの確認）。
                ただし<b>まだ締まっていない月の入力遅れ（手術の会計入力が数日後になる分）はこの差では測れません</b>。直近の数日は後から増えることがあります。</>
            )}
          </li>
          <li>全身麻酔（必須）は「患者×実施日」、6手術要件は「RP」で数えており単位が揃っていない（通知・様式に数え方の明文なし）。届出前に地方厚生局へ確認が必要な院内解釈です。</li>
          <li>令和8年3月31日時点で急性期充実体制加算1（または総合入院体制加算1）を届け出ていた医療機関は、令和9年5月31日までの間、ア〜キの基準を満たすものとみなされます（経過措置 (16)）。全身麻酔件数も急性期充実体制加算の届出医療機関は同日までみなされます（同 (20)）。当院は急性期充実体制加算1の届出医療機関でした。みなしが切れた後（令和9年6月〜）の判定には2026年度の実績が使われます。</li>
          <li>令和8年5月31日までの全身麻酔は改定前の L008（マスク又は気管挿管）で数えてよい経過措置 (19) があり、この集計はそれに沿っています。</li>
          <li>件数は{m.server}から集計（取得 {m.fetchedAt}）。個人を特定できる情報は持っていません。</li>
        </ul>
      </section>
    </div>
  );
}
